import { useEffect, useRef, useState } from "react"
import { debugApi, type ActivityLogDTO, type DebugLiveDTO, type DebugQueueDTO, type DebugStatsDTO } from "@/lib/api"

// ── helpers ───────────────────────────────────────────────────────────────────

function fmt(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`
}

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
  return new Date(iso).toLocaleTimeString()
}

// ── Live Queue ────────────────────────────────────────────────────────────────

function LiveQueue() {
  const [live, setLive] = useState<DebugLiveDTO | null>(null)
  const [queue, setQueue] = useState<DebugQueueDTO | null>(null)

  useEffect(() => {
    let mounted = true

    const load = async () => {
      try {
        const [l, q] = await Promise.all([debugApi.live(), debugApi.queue()])
        if (mounted) { setLive(l); setQueue(q) }
      } catch { /* ignore */ }
    }

    load()

    const interval = setInterval(load, 3000)
    return () => { mounted = false; clearInterval(interval) }
  }, [])

  const active = live?.active_jobs ?? []
  const queued = queue?.queued ?? []

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Live Queue</h2>

      {active.length === 0 && queued.length === 0 && (
        <p className="text-sm text-muted-foreground">No jobs running or queued.</p>
      )}

      {active.map(job => (
        <div key={job.job_id} className="rounded-lg border bg-card p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-medium">Worker {job.worker_id} — Job #{job.job_id}</span>
              <span className="text-xs text-muted-foreground">tenant {job.tenant_id}</span>
            </div>
            <span className="text-xs text-muted-foreground">{job.elapsed_s}s elapsed</span>
          </div>
          {job.sku && (
            <div className="text-xs text-muted-foreground">
              Generating <span className="font-mono text-foreground">{job.sku}</span>
              {job.type_ && <> — <span className="font-medium">{job.type_}</span></>}
            </div>
          )}
          {job.total > 0 && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{job.done}/{job.total}</span>
                <span>{Math.round((job.done / job.total) * 100)}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${Math.round((job.done / job.total) * 100)}%` }}
                />
              </div>
            </div>
          )}
          {job.model && (
            <div className="text-xs text-muted-foreground">
              {job.provider} / {job.model}
            </div>
          )}
        </div>
      ))}

      {queued.length > 0 && (
        <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase">Waiting ({queued.length})</p>
          {queued.map((item, i) => (
            <div key={item.job_id} className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground w-4 text-right">{i + 1}.</span>
              <span>{item.job_name}</span>
              <span className="text-muted-foreground text-xs">— {item.tenant_name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Stats ─────────────────────────────────────────────────────────────────────

function Stats() {
  const [stats, setStats] = useState<DebugStatsDTO | null>(null)

  useEffect(() => {
    debugApi.stats().then(setStats).catch(() => {})
    const interval = setInterval(() => debugApi.stats().then(setStats).catch(() => {}), 30000)
    return () => clearInterval(interval)
  }, [])

  if (!stats) return <p className="text-sm text-muted-foreground">Loading stats…</p>

  const { today } = stats

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Today's Stats</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: "Jobs completed", value: today.jobs_completed },
          { label: "Generations", value: today.total_generations },
          { label: "Errors", value: today.errors },
          { label: "Avg duration", value: fmt(today.avg_duration_ms) },
        ].map(({ label, value }) => (
          <div key={label} className="rounded-lg border bg-card p-3">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-xl font-semibold mt-0.5">{value}</p>
          </div>
        ))}
      </div>
      {Object.keys(today.models_used).length > 0 && (
        <div className="rounded-lg border bg-card p-3">
          <p className="text-xs text-muted-foreground mb-2">Models used today</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(today.models_used).map(([model, count]) => (
              <span key={model} className="text-xs rounded-full bg-muted px-2 py-0.5 font-mono">
                {model} × {count}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Activity Feed ─────────────────────────────────────────────────────────────

const ACTION_COLORS: Record<string, string> = {
  "auth.login":          "text-green-600",
  "auth.login_failed":   "text-red-500",
  "job.process.done":    "text-blue-500",
  "job.process.enqueue": "text-yellow-500",
  "job.delete":          "text-red-400",
  "job.create":          "text-muted-foreground",
}

function ActivityFeed() {
  const [rows, setRows] = useState<ActivityLogDTO[]>([])
  const [filterAction, setFilterAction] = useState("")
  const [filterTenant, setFilterTenant] = useState("")
  const [loading, setLoading] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const data = await debugApi.activity({
        action: filterAction || undefined,
        limit: 100,
      })
      const filtered = filterTenant
        ? data.filter(r => r.tenant_name?.toLowerCase().includes(filterTenant.toLowerCase()))
        : data
      setRows(filtered)
    } catch { /* ignore */ }
    finally { setLoading(false) }
  }

  useEffect(() => { load() }, [filterAction, filterTenant]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh every 5s
  useEffect(() => {
    const interval = setInterval(load, 5000)
    return () => clearInterval(interval)
  }, [filterAction, filterTenant]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Activity Feed</h2>
        <div className="flex gap-2">
          <input
            className="h-7 rounded-md border bg-background px-2 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            placeholder="Filter by action…"
            value={filterAction}
            onChange={e => setFilterAction(e.target.value)}
          />
          <input
            className="h-7 rounded-md border bg-background px-2 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            placeholder="Filter by client…"
            value={filterTenant}
            onChange={e => setFilterTenant(e.target.value)}
          />
        </div>
      </div>

      {loading && rows.length === 0 && <p className="text-sm text-muted-foreground">Loading…</p>}

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground">Time</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground">Client</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground">Action</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground">Details</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground">IP</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} className="border-t hover:bg-muted/20 transition-colors">
                <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">{relTime(row.created_at)}</td>
                <td className="px-3 py-1.5 font-medium">{row.tenant_name ?? <span className="text-muted-foreground italic">admin</span>}</td>
                <td className={`px-3 py-1.5 font-mono ${ACTION_COLORS[row.action] ?? "text-foreground"}`}>{row.action}</td>
                <td className="px-3 py-1.5 text-muted-foreground max-w-xs truncate">
                  {row.metadata ? (
                    <MetaPreview meta={row.metadata} />
                  ) : "—"}
                </td>
                <td className="px-3 py-1.5 font-mono text-muted-foreground">{row.ip_address ?? "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-muted-foreground">No activity found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function MetaPreview({ meta }: { meta: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState(false)
  const preview = Object.entries(meta)
    .slice(0, 3)
    .map(([k, v]) => `${k}:${JSON.stringify(v)}`)
    .join(" ")

  if (expanded) {
    return (
      <span>
        <pre className="whitespace-pre-wrap text-[10px] font-mono">{JSON.stringify(meta, null, 2)}</pre>
        <button className="text-primary hover:underline" onClick={() => setExpanded(false)}>less</button>
      </span>
    )
  }
  return (
    <span>
      {preview}
      {Object.keys(meta).length > 3 && (
        <button className="ml-1 text-primary hover:underline" onClick={() => setExpanded(true)}>…more</button>
      )}
    </span>
  )
}

// ── Live Event Log ────────────────────────────────────────────────────────────

interface LiveEvent { id: number; event: string; data: Record<string, unknown>; ts: Date }

function LiveEventLog() {
  const [events, setEvents] = useState<LiveEvent[]>([])
  const counterRef = useRef(0)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => { void counterRef }, [])

  const eventColor: Record<string, string> = {
    worker_start: "text-green-500",
    worker_done:  "text-blue-500",
    enqueue:      "text-yellow-500",
    progress:     "text-muted-foreground",
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Live Event Stream</h2>
        <button
          className="text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setEvents([])}
        >
          Clear
        </button>
      </div>
      <div
        ref={listRef}
        className="rounded-lg border bg-[#0d0d0d] font-mono text-xs p-3 h-64 overflow-y-auto space-y-0.5"
      >
        {events.length === 0 && (
          <span className="text-muted-foreground">Waiting for events…</span>
        )}
        {events.map(ev => (
          <div key={ev.id} className="flex gap-2">
            <span className="text-muted-foreground shrink-0">
              {ev.ts.toLocaleTimeString()}
            </span>
            <span className={`shrink-0 ${eventColor[ev.event] ?? "text-foreground"}`}>{ev.event}</span>
            <span className="text-muted-foreground truncate">{JSON.stringify(ev.data)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function DebugPage() {
  return (
    <div className="space-y-8 max-w-5xl">
      <Stats />
      <LiveQueue />
      <LiveEventLog />
      <ActivityFeed />
    </div>
  )
}
